import React from 'react';
import { useAuth } from '../../../context/AuthContext';
import styles from './ChatHeader.module.css';

/** Derive a friendly first name from the auth context.
 *  Falls back to email local-part → "there". */
const useFirstName = (): string => {
  const { user } = useAuth();
  // Admin sessions: user is null; read admin email from auth_meta
  const adminEmail = React.useMemo(() => {
    try {
      const raw = window.localStorage.getItem('auth_meta');
      if (!raw) return null;
      const p = JSON.parse(raw);
      return p?.is_admin ? (p?.email ?? p?.username ?? null) : null;
    } catch { return null; }
  }, []);

  const source = user?.full_name || user?.email || adminEmail || '';
  if (!source) return 'there';
  const cleaned = source.includes('@') ? source.split('@')[0] : source;
  const first = cleaned.replace(/[._-]+/g, ' ').trim().split(/\s+/)[0];
  if (!first) return 'there';
  return first.charAt(0).toUpperCase() + first.slice(1);
};

/** Reference HTML uses 4 colored icon tones: blue/teal/orange/purple */
type SuggestionTone = 'blue' | 'teal' | 'orange' | 'purple';

interface Suggestion {
  title: string;
  subtitle: string;
  tone: SuggestionTone;
  icon: React.ReactNode;
  // Query actually sent to the chat. Defaults to `title`, but the comparison /
  // impact prompts are AI-implicit ("Compare requirements across jurisdictions")
  // — without an explicit "AI" + regulatory + breadth signal the backend's
  // verified-facts grounding doesn't fire and the model answers from stale
  // memory (QA: confused AI policy with the EO 14081 biotech order, called the
  // EU AI Act a "proposal"). Sending an AI-explicit query grounds the answer.
  prompt?: string;
}

/* Minimal inline icons matching the reference (file / scale / chart / bank) */
const FileIcon = () => (
  <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
    <path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z"/><polyline points="14 2 14 8 20 8"/><line x1="8" y1="13" x2="16" y2="13"/><line x1="8" y1="17" x2="14" y2="17"/>
  </svg>
);
const ScaleIcon = () => (
  <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
    <path d="M12 3v18M6 7l-3 6h6zM18 7l3 6h-6zM3 13a3 3 0 006 0M15 13a3 3 0 006 0"/>
  </svg>
);
const ChartIcon = () => (
  <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
    <line x1="12" y1="20" x2="12" y2="10"/><line x1="18" y1="20" x2="18" y2="4"/><line x1="6" y1="20" x2="6" y2="14"/>
  </svg>
);
const BankIcon = () => (
  <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
    <path d="M3 21h18M3 10h18M5 6l7-4 7 4M6 10v8M10 10v8M14 10v8M18 10v8"/>
  </svg>
);

const ArrowIcon = () => (
  <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
    <line x1="5" y1="12" x2="19" y2="12"/><polyline points="12 5 19 12 12 19"/>
  </svg>
);

const SUGGESTIONS: Suggestion[] = [
  { title: 'Summarize recent AI regulations',          subtitle: 'Policy summary',      tone: 'blue',   icon: <FileIcon />, prompt: 'Summarize recent AI regulations' },
  { title: 'Compare requirements across jurisdictions', subtitle: 'Comparison',          tone: 'teal',   icon: <ScaleIcon />, prompt: 'Compare AI regulation requirements across major jurisdictions' },
  { title: 'Analyze policy impact on startups',         subtitle: 'Impact analysis',     tone: 'orange', icon: <ChartIcon />, prompt: 'Analyze the impact of current AI regulations on startups' },
  { title: 'Track federal AI legislation updates',      subtitle: 'Legislation tracker', tone: 'purple', icon: <BankIcon />, prompt: 'Track recent federal AI legislation updates' },
];

interface ChatHeaderProps {
  onSuggestion?: (text: string) => void;
}

export const ChatHeader: React.FC<ChatHeaderProps> = ({ onSuggestion }) => {
  const firstName = useFirstName();
  return (
    <div className={styles.centeredWelcome}>
      <div className={styles.heroOrb1} aria-hidden="true" />
      <div className={styles.heroOrb2} aria-hidden="true" />

      <div className={styles.welcomeBlock}>
        {/* AI badge — reference exact: white pill, blue border, blue dot pulsing */}
        <div className={styles.aiBadge}>
          <span className={styles.aiBadgeDot} aria-hidden="true" />
          POLICY INTELLIGENCE · AI ASSISTANT
        </div>

        {/* Headline — name in BLUE per reference; uses real logged-in user */}
        <h1 className={styles.welcomeTitle}>
          Welcome back, <span className={styles.welcomeName}>{firstName}</span>
        </h1>

        <p className={styles.welcomeGreeting}>
          Ask anything about global regulations, policy changes,
          <br />
          compliance requirements, or jurisdictional updates.
        </p>

        {/* Prompt cards — 2×2 grid, icon + 2-line content + arrow */}
        <div className={styles.suggestionGrid}>
          {SUGGESTIONS.map((s) => (
            <button
              key={s.title}
              className={styles.suggestionCard}
              onClick={() => onSuggestion?.(s.prompt ?? s.title)}
              type="button"
            >
              <span
                className={`${styles.suggestionIcon} ${styles[`tone_${s.tone}`]}`}
                aria-hidden="true"
              >
                {s.icon}
              </span>
              <span className={styles.suggestionContent}>
                <span className={styles.suggestionTitle}>{s.title}</span>
                <span className={styles.suggestionSubtitle}>{s.subtitle}</span>
              </span>
              <span className={styles.suggestionArrow} aria-hidden="true">
                <ArrowIcon />
              </span>
            </button>
          ))}
        </div>
      </div>
    </div>
  );
};
